import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api.js';

// Response shapes for the 4c pricing endpoints. Declared here (web-side) because
// the API keeps these DTOs feature-local; promoting them to @admitly/shared would
// mean editing a backend file, which this frontend brief must not do. They mirror
// apps/api/src/features/pricing/pricing.service.ts exactly.

export type PricingAgencyFee = {
  tuitionCents: number;
  validFrom: string; // YYYY-MM-DD
  createdAt: string; // ISO 8601
};

export type ScholarshipKind = 'percent' | 'amount' | 'none';

/**
 * The current scholarship embedded on a list row (5b). `null` on the row means
 * never set; kind 'none' is a real stored version meaning "removed".
 */
export type PricingScholarship = {
  kind: ScholarshipKind;
  percentBp: number | null; // set only for kind 'percent' (basis points, 1000 = 10%)
  amountCents: number | null; // set only for kind 'amount'
  validFrom: string; // YYYY-MM-DD
  createdAt: string; // ISO 8601
};

export type PricingCourseRow = {
  courseId: string;
  name: string;
  cricosCode: string | null;
  providerName: string;
  category: string;
  subCategory: string;
  aqfLevel: string | null;
  publishedAnnualTuitionCents: number | null;
  publishedTotalProgramCents: number | null;
  agencyFee: PricingAgencyFee | null;
  // The API always sends the two fields below (5b). They are optional here only
  // so pre-scholarship row fixtures keep typechecking; treat undefined as null.
  scholarship?: PricingScholarship | null;
  netFeeCents?: number | null; // server-computed; null iff no current fee; may be negative
};

export type PricingCourseListResponse = {
  asOf: string; // YYYY-MM-DD the current fees were resolved as-of
  items: PricingCourseRow[];
};

export type CourseFeeVersion = {
  id: string;
  tuitionCents: number;
  validFrom: string; // YYYY-MM-DD
  createdBy: string | null; // raw user id
  createdByName: string | null; // editor's real name, resolved server-side; null iff createdBy is null
  createdAt: string; // ISO 8601
};

export type CourseFeeResponse = {
  id: string;
  masterCourseId: string;
  tuitionCents: number;
  validFrom: string;
  createdBy: string | null;
  createdAt: string;
};

export type CourseScholarshipVersion = {
  id: string;
  kind: ScholarshipKind;
  percentBp: number | null;
  amountCents: number | null;
  validFrom: string; // YYYY-MM-DD
  createdBy: string | null; // raw user id
  createdByName: string | null; // editor's real name, resolved server-side; null iff createdBy is null
  createdAt: string; // ISO 8601
};

/**
 * POST body for a scholarship save (5b). Off-kind value fields must be OMITTED
 * (the API rejects extras); validFrom is omitted too — the server defaults it
 * to today, and this screen has no future-dating.
 */
export type SetCourseScholarshipBody =
  | { kind: 'percent'; percentBp: number } // 1..10000 bp
  | { kind: 'amount'; amountCents: number } // > 0
  | { kind: 'none' };

const pricingKey = ['admin', 'pricing', 'courses'] as const;
const historyKey = (masterCourseId: string) => [...pricingKey, masterCourseId, 'history'] as const;
const scholarshipHistoryKey = (masterCourseId: string) =>
  [...pricingKey, masterCourseId, 'scholarships'] as const;

/** Every visible course with its published fee + current agency fee (4c GET /courses). */
export function usePricingCourses() {
  return useQuery({
    queryKey: pricingKey,
    queryFn: () => apiGet<PricingCourseListResponse>('/admin/pricing/courses'),
    staleTime: 30_000,
  });
}

/** One course's fee version history, newest-first. Disabled until a course is selected. */
export function useCourseFeeHistory(masterCourseId: string | null) {
  return useQuery({
    queryKey: masterCourseId ? historyKey(masterCourseId) : [...pricingKey, '__none__', 'history'],
    queryFn: () =>
      apiGet<{ history: CourseFeeVersion[] }>(`/admin/pricing/courses/${masterCourseId}/fees`),
    enabled: !!masterCourseId,
  });
}

/** Set a new fee version (4c POST). Refreshes both the list row and the history. */
export function useSetCourseFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      masterCourseId,
      tuitionCents,
    }: {
      masterCourseId: string;
      tuitionCents: number;
    }) =>
      apiPost<{ fee: CourseFeeResponse }>(`/admin/pricing/courses/${masterCourseId}/fees`, {
        tuitionCents,
      }),
    onSuccess: (_data, { masterCourseId }) => {
      qc.invalidateQueries({ queryKey: pricingKey });
      qc.invalidateQueries({ queryKey: historyKey(masterCourseId) });
    },
  });
}

/** One course's scholarship version history, newest-first (5b GET). Disabled until selected. */
export function useCourseScholarshipHistory(masterCourseId: string | null) {
  return useQuery({
    queryKey: masterCourseId
      ? scholarshipHistoryKey(masterCourseId)
      : [...pricingKey, '__none__', 'scholarships'],
    queryFn: () =>
      apiGet<{ history: CourseScholarshipVersion[] }>(
        `/admin/pricing/courses/${masterCourseId}/scholarships`,
      ),
    enabled: !!masterCourseId,
  });
}

/**
 * Set, change or remove the scholarship (5b POST). Refreshes the course list
 * (scholarship + server-computed net fee) and the scholarship history, so the
 * screen always shows the server's numbers.
 */
export function useSetCourseScholarship() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      masterCourseId,
      body,
    }: {
      masterCourseId: string;
      body: SetCourseScholarshipBody;
    }) =>
      // The POST returns the stored version WITHOUT the name join (that's a
      // history-read concern), so it must not claim createdByName.
      apiPost<{ scholarship: Omit<CourseScholarshipVersion, 'createdByName'> }>(
        `/admin/pricing/courses/${masterCourseId}/scholarships`,
        body,
      ),
    onSuccess: (_data, { masterCourseId }) => {
      qc.invalidateQueries({ queryKey: pricingKey });
      qc.invalidateQueries({ queryKey: scholarshipHistoryKey(masterCourseId) });
    },
  });
}
